import { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, Download, Share2, AlertCircle, Calendar, Hash, MessageCircle, Instagram, Copy, Twitter } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { toPng } from "html-to-image";
import { API } from "@/utils/api";

const ProofPage = () => {
  const { certificateId } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [certificate, setCertificate] = useState(null);
  const [error, setError] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const certificateRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchCertificate();
  }, [certificateId]);

  const fetchCertificate = async () => {
    try {
      const response = await axios.get(`${API}/certificates/${certificateId}`);
      setCertificate(response.data);
    } catch (err) {
      console.error("Error fetching certificate:", err);
      if (err.response?.status === 402) {
        setError("Payment required to view this certificate.");
      } else {
        setError(err.response?.data?.detail || "Certificate not found.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadImage = async () => {
    setIsDownloading(true);
    try {
      // Use client-side HTML-to-image generation - High quality
      if (certificateRef.current) {
        const dataUrl = await toPng(certificateRef.current, {
          quality: 1,
          pixelRatio: 3, // High quality for crisp images
          backgroundColor: '#FCF8F7',
          cacheBust: true,
        });
        
        // Create download link
        const link = document.createElement('a');
        link.download = `LoBe-Certificate-${certificate.your_name}-${certificate.partner_name}.png`;
        link.href = dataUrl;
        link.click();
        
        toast.success("Certificate downloaded successfully!");
      } else {
        throw new Error("Certificate element not found");
      }
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Failed to download certificate. Please try again.");
    } finally {
      setIsDownloading(false);
    }
  };

  const getShareUrl = () => {
    return `${window.location.origin}/share/${certificate.certificate_id}`;
  };

  const shareToWhatsApp = () => {
    const url = encodeURIComponent(getShareUrl());
    const text = encodeURIComponent(`Check out this fun certificate! 🎉\n${certificate.your_name} & ${certificate.partner_name}`);
    window.open(`https://wa.me/?text=${text}%20${url}`, '_blank');
  };

  const shareToTwitter = () => {
    const url = encodeURIComponent(getShareUrl());
    const text = encodeURIComponent(`Just got a LoBe Certificate of Love! 💕 #LoBe #CertificateOfLove`);
    window.open(`https://twitter.com/intent/tweet?text=${text}&url=${url}`, '_blank');
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(getShareUrl());
      toast.success("Link copied to clipboard!");
    } catch (error) {
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = getShareUrl();
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      toast.success("Link copied to clipboard!");
    }
  };

  const shareToInstagram = async () => {
    // Instagram does NOT support direct web sharing URLs
    // Best approach: Download certificate, then guide user to share
    toast.info("Preparing your certificate for Instagram...");
    
    try {
      // Download the certificate first
      if (certificateRef.current) {
        const dataUrl = await toPng(certificateRef.current, {
          quality: 1,
          pixelRatio: 3, // High quality
          backgroundColor: '#F9F6F2',
          cacheBust: true,
        });
        
        // Create download link
        const link = document.createElement('a');
        link.download = `LoBe-Certificate-${certificate.your_name}-${certificate.partner_name}.png`;
        link.href = dataUrl;
        link.click();
        
        // Check if on mobile
        const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
        
        setTimeout(() => {
          if (isMobile) {
            // Try to open Instagram app
            window.location.href = 'instagram://library?AssetPath=' + encodeURIComponent(link.download);
            
            // Fallback
            setTimeout(() => {
              toast.success("Certificate saved! Open Instagram and share from your gallery.", { duration: 5000 });
            }, 1000);
          } else {
            // Desktop - guide user
            toast.success("Certificate downloaded! Upload it to Instagram from your computer.", { duration: 5000 });
            window.open('https://www.instagram.com/', '_blank');
          }
        }, 500);
      }
    } catch (error) {
      console.error("Instagram share error:", error);
      toast.error("Failed to prepare certificate. Please try downloading manually.");
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short'
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="proof-loading">
        <div className="text-center">
          <Heart className="w-12 h-12 text-[#E8A0BF] animate-pulse mx-auto mb-4" />
          <p className="text-[#5A5A5A]">Loading certificate...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 sm:px-6" data-testid="proof-error">
        <div className="text-center max-w-md">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-[#2D2D2D] mb-2">Certificate Not Available</h2>
          <p className="text-[#5A5A5A] mb-6">{error}</p>
          <Link to="/create">
            <Button className="bg-[#E8A0BF] hover:bg-[#D68C85] text-white rounded-full px-6">
              Create Your Certificate
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 md:py-16 px-4 sm:px-6 md:px-12 lg:px-24" data-testid="proof-page">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* Header */}
          <div className="text-center mb-6 sm:mb-8">
            <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 bg-green-100 rounded-full text-xs sm:text-sm text-green-700 mb-3 sm:mb-4">
              <Heart className="w-3 h-3 sm:w-4 sm:h-4 fill-green-600" />
              <span>Certificate Ready!</span>
            </div>
            <h1 
              className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#2D2D2D]"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Your Certificate is Ready!
            </h1>
          </div>

          {/* Certificate Display - Client-side generated */}
          <div className="mb-6 sm:mb-8">
            {/* Visible Certificate Preview */}
            <div className="overflow-hidden rounded-xl sm:rounded-2xl md:rounded-3xl shadow-lg sm:shadow-xl md:shadow-2xl" data-testid="certificate-display">
              <div 
                ref={certificateRef}
                className="certificate-wrapper mx-auto"
                style={{
                  width: '100%',
                  maxWidth: '1344px',
                  aspectRatio: '1344 / 720',
                  backgroundColor: '#F9F6F2',
                  display: 'flex',
                  position: 'relative',
                  fontFamily: "'Playfair Display', serif",
                  overflow: 'hidden',
                }}
              >
                {/* Left Sidebar - 4 bars */}
                <div className="hidden sm:flex" style={{
                  width: '4%',
                  height: '100%',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '8px',
                  paddingLeft: '12px',
                }}>
                  <div style={{ width: '12px', height: '80px', backgroundColor: '#B4D2E7', borderRadius: '6px' }} />
                  <div style={{ width: '12px', height: '80px', backgroundColor: '#E8A0BF', borderRadius: '6px' }} />
                  <div style={{ width: '12px', height: '80px', backgroundColor: '#B4D2E7', borderRadius: '6px' }} />
                  <div style={{ width: '12px', height: '80px', backgroundColor: '#E8A0BF', borderRadius: '6px' }} />
                </div>

                {/* Mobile Sidebars */}
                <div className="flex sm:hidden" style={{
                  width: '3%',
                  height: '100%',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '4px',
                  paddingLeft: '4px',
                }}>
                  <div style={{ width: '6px', height: '40px', backgroundColor: '#B4D2E7', borderRadius: '3px' }} />
                  <div style={{ width: '6px', height: '40px', backgroundColor: '#E8A0BF', borderRadius: '3px' }} />
                  <div style={{ width: '6px', height: '40px', backgroundColor: '#B4D2E7', borderRadius: '3px' }} />
                  <div style={{ width: '6px', height: '40px', backgroundColor: '#E8A0BF', borderRadius: '3px' }} />
                </div>

                {/* Main Content */}
                <div style={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 'clamp(10px, 3vw, 20px) clamp(15px, 4vw, 30px)',
                  textAlign: 'center',
                }}>
                  {/* Title */}
                  <h1 style={{
                    fontFamily: "'Playfair Display', serif",
                    fontSize: 'clamp(16px, 4vw, 42px)',
                    fontWeight: '700',
                    color: '#2D2D2D',
                    margin: '0 0 2px 0',
                    letterSpacing: 'clamp(1px, 0.3vw, 4px)',
                  }}>
                    Certificate of Love
                  </h1>
                  
                  {/* Subtitle */}
                  <p style={{
                    fontFamily: "'Playfair Display', serif",
                    fontSize: 'clamp(8px, 1.5vw, 16px)',
                    fontWeight: '500',
                    letterSpacing: 'clamp(1px, 0.3vw, 3px)',
                    color: '#5A5A5A',
                    margin: '0 0 clamp(8px, 2vw, 20px) 0',
                    textTransform: 'uppercase',
                  }}>
                    Official Declaration
                  </p>

                  {/* This certifies that */}
                  <p style={{
                    fontFamily: "'Manrope', sans-serif",
                    fontSize: 'clamp(8px, 1.2vw, 14px)',
                    fontWeight: '400',
                    color: '#888888',
                    margin: '0 0 clamp(4px, 1vw, 12px) 0',
                  }}>
                    This certifies that
                  </p>

                  {/* Names */}
                  <h2 style={{
                    fontFamily: "'Great Vibes', cursive",
                    fontSize: 'clamp(18px, 4vw, 44px)',
                    fontWeight: '400',
                    color: '#C97F84',
                    margin: '0 0 clamp(2px, 0.5vw, 6px) 0',
                    lineHeight: '1.2',
                  }}>
                    {certificate.your_name} & {certificate.partner_name}
                  </h2>

                  {/* Have declared their love */}
                  <p style={{
                    fontFamily: "'Manrope', sans-serif",
                    fontSize: 'clamp(8px, 1.2vw, 14px)',
                    fontWeight: '400',
                    color: '#888888',
                    margin: '0 0 clamp(4px, 1vw, 12px) 0',
                  }}>
                    Have declared their love on this day
                  </p>

                  {/* Divider Line */}
                  <div style={{
                    width: 'clamp(60px, 15vw, 180px)',
                    height: '2px',
                    backgroundColor: '#C97F84',
                    margin: '0 0 clamp(6px, 1.5vw, 16px) 0',
                  }} />

                  {/* Description */}
                  <p style={{
                    fontFamily: "'Playfair Display', serif",
                    fontSize: 'clamp(8px, 1.4vw, 16px)',
                    fontWeight: '400',
                    fontStyle: 'italic',
                    color: '#5A5A5A',
                    margin: '0 0 clamp(8px, 2vw, 20px) 0',
                    maxWidth: '90%',
                    lineHeight: '1.4',
                  }}>
                    "{certificate.description}"
                  </p>

                  {/* Footer with Date and ID */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 'clamp(10px, 3vw, 40px)',
                    fontFamily: "'Manrope', sans-serif",
                    fontSize: 'clamp(6px, 1.1vw, 13px)',
                    color: '#888888',
                    flexWrap: 'wrap',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <span>{formatDate(certificate.created_at).split(' at ')[0]}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <span style={{ fontWeight: '600' }}>#</span>
                      <span style={{ fontFamily: 'monospace' }}>{certificate.certificate_id}</span>
                    </div>
                  </div>

                  {/* LoBe Logo - Simple like homepage */}
                  <div style={{
                    position: 'absolute',
                    bottom: 'clamp(15px, 5vw, 40px)',
                    right: 'clamp(20px, 6vw, 80px)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'clamp(4px, 1vw, 8px)',
                  }}>
                    {/* Heart Icon - SVG */}
                    <svg 
                      width="clamp(16px, 3vw, 32px)" 
                      height="clamp(16px, 3vw, 32px)" 
                      viewBox="0 0 24 24" 
                      fill="#E8A0BF"
                      style={{ filter: 'drop-shadow(0 2px 4px rgba(232, 160, 191, 0.3))' }}
                    >
                      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                    </svg>
                    {/* LoBe Text */}
                    <span style={{
                      fontFamily: "'Playfair Display', serif",
                      fontSize: 'clamp(12px, 2vw, 24px)',
                      fontWeight: '700',
                      letterSpacing: '1px',
                    }}>
                      <span style={{ color: '#2D2D2D' }}>Lo</span>
                      <span style={{ color: '#E8A0BF' }}>Be</span>
                    </span>
                  </div>
                </div>

                {/* Right Sidebar - 4 bars - Desktop */}
                <div className="hidden sm:flex" style={{
                  width: '4%',
                  height: '100%',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '8px',
                  paddingRight: '12px',
                }}>
                  <div style={{ width: '12px', height: '80px', backgroundColor: '#B4D2E7', borderRadius: '6px' }} />
                  <div style={{ width: '12px', height: '80px', backgroundColor: '#E8A0BF', borderRadius: '6px' }} />
                  <div style={{ width: '12px', height: '80px', backgroundColor: '#B4D2E7', borderRadius: '6px' }} />
                  <div style={{ width: '12px', height: '80px', backgroundColor: '#E8A0BF', borderRadius: '6px' }} />
                </div>

                {/* Mobile Right Sidebar */}
                <div className="flex sm:hidden" style={{
                  width: '3%',
                  height: '100%',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '4px',
                  paddingRight: '4px',
                }}>
                  <div style={{ width: '6px', height: '40px', backgroundColor: '#B4D2E7', borderRadius: '3px' }} />
                  <div style={{ width: '6px', height: '40px', backgroundColor: '#E8A0BF', borderRadius: '3px' }} />
                  <div style={{ width: '6px', height: '40px', backgroundColor: '#B4D2E7', borderRadius: '3px' }} />
                  <div style={{ width: '6px', height: '40px', backgroundColor: '#E8A0BF', borderRadius: '3px' }} />
                </div>
              </div>
            </div>
          </div>

          {/* Download Button Only - No Copy Link next to it */}
          <div className="flex justify-center mb-6 sm:mb-8">
            <Button
              onClick={handleDownloadImage}
              disabled={isDownloading}
              className="bg-[#E8A0BF] hover:bg-[#D68C85] text-white rounded-full px-8 py-5 sm:py-6 shadow-lg hover:shadow-xl transition-all group flex items-center justify-center gap-2 text-sm sm:text-base"
              data-testid="download-btn"
            >
              {isDownloading ? (
                <>
                  <span className="animate-spin">⏳</span>
                  Downloading...
                </>
              ) : (
                <>
                  <Download className="w-5 h-5 group-hover:scale-110 transition-transform" />
                  Download Certificate (PNG)
                </>
              )}
            </Button>
          </div>

          {/* Share Section */}
          <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg p-4 sm:p-6 md:p-8 border border-[#E2E2E2]/50">
            <div className="text-center mb-4 sm:mb-6">
              <h3 className="text-lg sm:text-xl font-semibold text-[#2D2D2D] mb-1 sm:mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                Share the Love
              </h3>
              <p className="text-xs sm:text-sm text-[#5A5A5A]">
                Share a view-only preview with friends
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row flex-wrap justify-center gap-2 sm:gap-4">
              <Button
                onClick={copyLink}
                className="bg-[#6B7280] hover:bg-[#4B5563] text-white flex items-center justify-center gap-2 rounded-full px-4 sm:px-5 py-2 sm:py-3 text-sm"
                data-testid="copy-link-btn"
              >
                <Copy className="w-4 h-4 sm:w-5 sm:h-5" />
                Copy Link
              </Button>
              
              <Button
                onClick={shareToWhatsApp}
                className="bg-[#25D366] hover:bg-[#128C7E] text-white flex items-center justify-center gap-2 rounded-full px-4 sm:px-5 py-2 sm:py-3 text-sm"
                data-testid="share-whatsapp-btn"
              >
                <MessageCircle className="w-4 h-4 sm:w-5 sm:h-5" />
                WhatsApp
              </Button>
              
              <Button
                onClick={shareToInstagram}
                className="bg-gradient-to-r from-[#833AB4] via-[#FD1D1D] to-[#F77737] hover:opacity-90 text-white flex items-center justify-center gap-2 rounded-full px-4 sm:px-5 py-2 sm:py-3 text-sm"
                data-testid="share-instagram-btn"
              >
                <Instagram className="w-4 h-4 sm:w-5 sm:h-5" />
                Instagram
              </Button>
              
              <Button
                onClick={shareToTwitter}
                className="bg-[#1DA1F2] hover:bg-[#0d8bd9] text-white flex items-center justify-center gap-2 rounded-full px-4 sm:px-5 py-2 sm:py-3 text-sm"
                data-testid="share-twitter-btn"
              >
                <Twitter className="w-4 h-4 sm:w-5 sm:h-5" />
                Twitter
              </Button>
            </div>

            <p className="text-xs text-[#888888] text-center mt-4">
              Shared links show a view-only preview (no download option).
            </p>
          </div>

          {/* Disclaimer */}
          <div className="mt-6 sm:mt-8 text-center text-[10px] sm:text-xs text-[#888888] bg-[#F4F1EA] rounded-xl p-3 sm:p-4">
            <p className="font-medium mb-1">Important Notice</p>
            <p>
              This certificate is for <strong>entertainment and novelty purposes only</strong>. 
              It has no legal validity and is not an official document.
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ProofPage;
